import { useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../../../theme/theme'
import { isIFAQ } from '@estercare/ester-shared'
import { useGetGeneralPage } from '../../../../api/contentful/contentfulQueries'
import {
  useGetHealthCallAppointmentQuery,
  useGetHealthCallSummaryQuery,
} from '../../../../api/ester-b2b-api/private/healthCalls/healthCallQueries'
import { FAQ } from '../../../../components/Faq'
import { Header } from '../../../../components/Header'
import { ComponentLayout } from '../../../../components/layouts/ComponentLayout'
import { LoadingSpinner } from '../../../../components/LoadingSpinner'
import { PreviewGuard } from '../../../../components/PreviewGuard'
import { ErrorGuard } from '../../../../components/ErrorGuard'
import { BodyLargeMedium, Title6 } from '../../../../components/styles/Typography'
import { ServiceGuide } from '../../../../components/ServiceGuide'
import { OnClickButton } from '../../../../components/buttons/onclick/OnClickButton'
import { QRCode } from '../../../../components/icons/QRCode'
import { TextInput } from '../../../../components/inputs/Text'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useSelectUserOrganisationName,
  useSelectUserBirthYear,
  useSelectPersonalNumber,
  useSelectHasPersonalNumber,
} from '../../../../state/selectors'
import { useHealthCallFormValidation } from '../../../../hooks/useHealthCallFormValidation'
import { JournalConsentCheckbox, useJournalConsent } from '../../../../components/consent/JournalConsent'
import { HealthCallBookingDetails } from '../../../../components/HealthCallBookingDetails'
import { ConsentId } from '../../../../api/ester-b2b-api/private/consent/types'
import { useHealthCallStore } from '../../../../state/store'
import { usePersonalNumber } from '../../../../hooks/usePersonalNumber'
import { useFilteredContent } from '../../../../hooks/useFilteredContent'
import { toDisplayPersonalNumber } from '../../../../utils/personalNumber'

export const HealthCall = () => {
  const { t } = useTranslation()
  const organisationName = useSelectUserOrganisationName()
  const birthYear = useSelectUserBirthYear()
  const userPersonalNumber = useSelectPersonalNumber()
  const hasPersonalNumber = useSelectHasPersonalNumber()
  const slug = useParams()
  const navigate = useNavigate()

  const { setBookingFormData, healthCallAppointment } = useHealthCallStore()
  const { data: healthCallPage, isLoading: isHealthCallPageLoading } = useGetGeneralPage(slug?.id || '')
  const {
    data: healthCallSummary,
    isLoading: isHealthCallSummaryLoading,
    isError: isHealthCallSummaryError,
    error: healthCallSummaryError,
  } = useGetHealthCallSummaryQuery()
  const {
    isLoading: isHealthCallAppointmentLoading,
    isError: isHealthCallAppointmentError,
    error: healthCallAppointmentError,
  } = useGetHealthCallAppointmentQuery(hasPersonalNumber)
  const { savePersonalNumber } = usePersonalNumber()

  const {
    hasConsent: hasJournalConsent,
    isLoading: isJournalConsentLoading,
    checked: journalChecked,
    setChecked: setJournalChecked,
    saveConsent: saveJournalConsent,
  } = useJournalConsent(ConsentId.HEALTH_CALL_JOURNAL)
  const [journalConsentError, setJournalConsentError] = useState('')

  const {
    formData,
    validationErrors,
    isFormValid,
    handlePersonalNumberChange,
    handlePhoneNumberChange,
    validateForm,
    setFieldError,
  } = useHealthCallFormValidation({
    initialValues: {
      personalNumber: userPersonalNumber ? toDisplayPersonalNumber(userPersonalNumber) : '',
    },
  })

  // Extract content blocks and filter them (must be called before any early returns)
  const healthCallPageContent = healthCallPage?.items?.[0]?.fields
  const { title, description, contentBlocks } = healthCallPageContent || {}
  const filteredContentBlocks = useFilteredContent(contentBlocks)

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    if (!hasJournalConsent) {
      try {
        await saveJournalConsent()
        setJournalConsentError('')
      } catch {
        setJournalConsentError(t('employee.healthCall.journalConsentError'))
        return
      }
    }
    setBookingFormData({
      personalNumber: formData.personalNumber,
      phoneNumber: formData.phoneNumber,
    })
    try {
      await savePersonalNumber({
        personalNumber: formData.personalNumber,
        isValid: !validationErrors.personalNumber,
        setError: (message) => setFieldError('personalNumber', message),
        t,
      })
      navigate('/portal/health-services/health-consultation/book')
    } catch {
      // Error is already displayed via setFieldError
    }
  }

  if (isHealthCallPageLoading || isHealthCallSummaryLoading || isHealthCallAppointmentLoading) {
    return <LoadingSpinner />
  }

  if (!healthCallPageContent) return <PreviewGuard />
  if (!title) return <PreviewGuard />

  if (!healthCallSummary || isHealthCallSummaryError) {
    return <ErrorGuard error={healthCallSummaryError} />
  }

  if (isHealthCallAppointmentError) {
    // Intentional logging for debugging - allows appointment booking even if fetch fails
    // eslint-disable-next-line no-console
    console.error('Failed to fetch health call appointment:', healthCallAppointmentError)
  }

  const canBookHealthCall = healthCallSummary.remaining > 0

  const healthCallText = t('employee.healthCall.callsRemaining', {
    count: healthCallSummary.remaining,
    total: healthCallSummary.total,
  })

  const steps = [
    {
      title: t('employee.healthCall.step1Title'),
      description: t('employee.healthCall.step1Description'),
    },
    {
      title: t('employee.healthCall.step2Title'),
      description: t('employee.healthCall.step2Description'),
      content: <QRCode />,
    },
    {
      title: t('employee.healthCall.step3Title'),
      description: t('employee.healthCall.step3Description'),
    },
  ]

  const completedText = {
    title: t('employee.healthCall.completedTitle', { total: healthCallSummary.total }),
    description: t('employee.healthCall.completedDescription', {
      organisationName: organisationName || t('employee.other.yourOrganisation'),
    }),
  }

  // Render booking form when health calls are available
  const renderBookingForm = () => (
    <ComponentLayout paddingTop={0}>
      <InputWrapper>
        <StyledInput
          label={t('employee.healthCall.personalNumber')}
          name="personnummer"
          value={formData.personalNumber}
          onChange={(e) => handlePersonalNumberChange(e.target.value)}
          type="text"
          placeholder={birthYear ? `${birthYear}XXXX-XXXX` : '19XXXXXX-XXXX'}
          inputMode="numeric"
        />
        {validationErrors.personalNumber && <ErrorMessage>{validationErrors.personalNumber}</ErrorMessage>}

        <Spacer />
        <StyledInput
          label={t('employee.healthCall.phoneNumber')}
          name="telefonnummer"
          value={formData.phoneNumber}
          onChange={(e) => handlePhoneNumberChange(e.target.value)}
          onFocus={(e) => {
            if (!e.target.value || e.target.value === '') {
              handlePhoneNumberChange('+46')
            }
          }}
          type="tel"
          placeholder="+4670XXXXXXX"
          inputMode="numeric"
        />
        {validationErrors.phoneNumber && <ErrorMessage>{validationErrors.phoneNumber}</ErrorMessage>}
        <Spacer space="32" />
        {!isJournalConsentLoading && !hasJournalConsent && (
          <>
            <JournalConsentCheckbox
              text={t('employee.healthCall.journalText')}
              label={t('employee.healthCall.journalCheckbox')}
              checked={journalChecked}
              onChange={setJournalChecked}
            />
            <Spacer space="24" />
          </>
        )}
        {journalConsentError && <ErrorMessage>{journalConsentError}</ErrorMessage>}
        <OnClickButton
          text={t('employee.healthCall.goToCalendar')}
          onClick={handleSubmit}
          disabled={isJournalConsentLoading || (!hasJournalConsent && !journalChecked) || !isFormValid}
        />
      </InputWrapper>
    </ComponentLayout>
  )

  // Render existing appointment details
  const renderAppointmentDetails = () => (
    <ComponentLayout
      paddingTop={theme.spacing.large}
      paddingBottom={theme.spacing.xxlarge}
      backgroundColor={theme.color.beigeDark}
    >
      <StyledTitle6>{t('employee.healthCall.bookingDetails')}</StyledTitle6>
      <HealthCallBookingDetails appointment={healthCallAppointment!} />
    </ComponentLayout>
  )

  // Render completed state when no health calls are available
  const renderCompletedState = () => (
    <ComponentLayout paddingTop={0}>
      <ServiceGuide text={completedText} />
    </ComponentLayout>
  )

  // Determine which content block to display based on current state
  const displayCorrectBlock = () => {
    if (isHealthCallAppointmentLoading) {
      return <LoadingSpinner color={theme.color.plum} size={64} />
    }

    if (healthCallAppointment) {
      return renderAppointmentDetails()
    }

    if (canBookHealthCall) {
      return renderBookingForm()
    }

    return renderCompletedState()
  }
  return (
    <>
      <Header
        title={title}
        description={description}
        backButtonLink="/portal"
        backgroundColor={theme.color.beigeLight}
        noPaddingBottom
      />
      <ComponentLayout paddingTop={0} paddingBottom={theme.spacing.medium}>
        <StyledBodyLargeMedium>{healthCallText}</StyledBodyLargeMedium>
      </ComponentLayout>
      {displayCorrectBlock()}
      {(canBookHealthCall || healthCallAppointment) && (
        <ComponentLayout paddingTop={theme.spacing.large}>
          <StyledTitle6>{t('employee.healthCall.howToPerform')}</StyledTitle6>
          <ServiceGuide steps={steps} />
        </ComponentLayout>
      )}
      <ComponentLayout>
        {filteredContentBlocks.map(
          (block, index) => isIFAQ(block) && <FAQ showSupportLink key={index} {...block.fields} />
        )}
      </ComponentLayout>
    </>
  )
}

const StyledTitle6 = styled(Title6)`
  margin-bottom: ${theme.spacing.medium}px;
`

const StyledBodyLargeMedium = styled(BodyLargeMedium)`
  margin: ${theme.spacing.medium}px 0;
`

const StyledInput = styled(TextInput)``

const ErrorMessage = styled.div`
  color: ${theme.color.red};
  font-size: ${theme.fontSize.small}px;
  margin-top: 4px;
  margin-bottom: 8px;
`

const Spacer = styled.div<{ space?: string }>`
  height: ${({ space = '16' }) => `${space}px`};
`
const InputWrapper = styled.div`
  max-width: 400px;
`
